import { ACCESS_MODEL, MEDIA_CONTENT_TYPE } from '../constants';
import type { AccessModel } from '../../types/config';
import type { MediaProduct } from '../../types/media';
import type { PlaylistItem } from '../../types/playlist';
import type { Pass, Subscription } from '../../types/subscription';

import { isFalsyCustomParamValue, isTruthyCustomParamValue, isContentType } from './common';

export const isFreeItem = (playlistItem: PlaylistItem) => {
  return isFalsyCustomParamValue(playlistItem?.requiresSubscription) || isTruthyCustomParamValue(playlistItem?.free);
};

export const getRelatedOfferIds = (subscription: Subscription | null, passes: Pass[], mediaOffers?: MediaProduct[] | null) => {
  const offerIds: string[] = [];

  if (subscription) {
    offerIds.push(subscription.offerId);
  }

  const allContentPass = passes?.find((pass) => pass.accessScope === 'all-content');
  if (allContentPass) {
    offerIds.push(allContentPass.offerId);
  }

  if (mediaOffers?.length) {
    offerIds.push(...mediaOffers.map((offer) => offer.id));
  }

  return offerIds;
};

const hasPassAccess = (playlistItem: PlaylistItem, passes: Pass[]) => {
  const mediaOffers = playlistItem?.mediaOffers;

  if (passes.some((pass) => pass.accessScope === 'all-content')) return true;
  if (!mediaOffers?.length || !passes.length) return false;

  return mediaOffers.some(({ id }) => passes.some((pass) => pass.offerId === id));
};

/**
 * The appearance of the lock icon, depending on the access model
 *
 * @param accessModel Platform AccessModel, excluding TVOD, which can only be applied per item
 * @param isLoggedIn
 * @param hasSubscription
 * @param playlistItem Used to define if the item is 'free' or has mediaOffers
 * @returns
 */
export const isLocked = (accessModel: AccessModel, isLoggedIn: boolean, hasSubscription: boolean, playlistItem: PlaylistItem, passes: Pass[] = []): boolean => {
  const mediaOffers = playlistItem?.mediaOffers;

  if (isFreeItem(playlistItem)) return false;
  if (isContentType(playlistItem, MEDIA_CONTENT_TYPE.hub)) return false;
  if (hasPassAccess(playlistItem, passes)) return false;
  if (accessModel === ACCESS_MODEL.AVOD && !mediaOffers) return false;
  if (accessModel === ACCESS_MODEL.AUTHVOD && isLoggedIn && !mediaOffers) return false;
  if (accessModel === ACCESS_MODEL.SVOD && hasSubscription && !mediaOffers?.some((offer) => offer.premier)) return false;

  return true;
};

/**
 * Filters MediaOffers from offers string
 *
 * @param offerIds String of comma separated key/value pairs, i.e. "cleeng:S916977979_NL, !cleeng:S91633379_NL, other_vendor:xyz123"
 * Key is vendor, value is the offerId.
 * Vendor keys starting with an exclamation mark represent a 'Premier Access' offer (TVOD only)
 *
 * @returns An array of MediaProduct { offerId, premier }
 */
export const filterMediaOffers = (vendorPrefix: string, offerIds?: string): MediaProduct[] | null => {
  if (!offerIds) return null;

  return offerIds
    .replace(/\s/g, '')
    .split(',')
    .reduce<MediaProduct[]>((offers, offerId) => {
      return offerId.indexOf(`${vendorPrefix}:`) === 0 || offerId.indexOf(`!${vendorPrefix}:`) === 0
        ? [...offers, { id: offerId.slice(offerId.indexOf(':') + 1), premier: offerId[0] === '!' }]
        : offers;
    }, []);
};
